import { useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';

/**
 * Hook pour récupérer les événements filtrés depuis Supabase
 * Applique les filtres côté serveur pour optimiser les performances
 */
export const useFilteredEvents = ({
  searchPosition,
  nearbyPostalCodes,
  filterDate,
  filterEndDate,
  filterCategories = [],
  filterAgeGroup = '',
  filterAllWeather = false,
  filterAccessible = false,
  filterFree = false,
  enabled = true
}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Créer une clé stable basée sur les codes postaux pour éviter les re-fetch inutiles
  const postalCodesKey = nearbyPostalCodes.map(pc => pc.code_postal).sort().join(',');
  const lastFetchKey = useRef('');

  useEffect(() => {
    const fetchEvents = async () => {
      // Conditions de garde
      if (!enabled) {
        console.log('[useFilteredEvents] Hook désactivé');
        setLoading(false);
        setEvents([]);
        return;
      }

      if (!searchPosition) {
        console.log('[useFilteredEvents] Pas de position de recherche');
        setLoading(false);
        setEvents([]);
        return;
      }

      if (nearbyPostalCodes.length === 0) {
        console.log('[useFilteredEvents] En attente du calcul des codes postaux...');
        setLoading(true);
        // Réinitialiser lastFetchKey quand les codes postaux disparaissent
        lastFetchKey.current = '';
        return;
      }

      // Créer une clé unique pour ce fetch
      const currentFetchKey = `${postalCodesKey}-${filterDate}-${filterEndDate}-${filterCategories.join(',')}-${filterAgeGroup}-${filterAllWeather}-${filterAccessible}-${filterFree}`;
      
      // Si on a déjà fetch avec exactement ces paramètres, ne pas refetch
      if (currentFetchKey === lastFetchKey.current) {
        console.log('[useFilteredEvents] ⏭️  Paramètres identiques - skip fetch');
        return;
      }

      lastFetchKey.current = currentFetchKey;
      console.log('[useFilteredEvents] 🔍 Début de la récupération des événements');
      
      setLoading(true);
      setError(null);
      
      try {
        const postalCodes = nearbyPostalCodes.map(pc => pc.code_postal);
        
        // console.log('[useFilteredEvents] 📤 Appel Edge Function avec:', {
        //   postalCodesCount: postalCodes.length,
        //   filterDate,
        //   filterEndDate
        // });

        // Appel à l'Edge Function SANS authentification
        const functionUrl = `${supabase.supabaseUrl}/functions/v1/get_published_events`;
        
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabase.supabaseKey, // Clé publique uniquement
            'x-client-info': 'maploop-web'
          },
          body: JSON.stringify({
            postalCodes,
            filterDate,
            filterEndDate,
            filterCategories: filterCategories.length > 0 ? filterCategories : undefined,
            filterAgeGroup: filterAgeGroup || null,
            filterAllWeather,
            filterAccessible,
            filterFree
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[useFilteredEvents] ❌ Erreur HTTP:', response.status, errorText);
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        const responseData = await response.json();

        if (responseData.error) {
          console.error('[useFilteredEvents] ❌ Erreur retournée:', responseData.error);
          setError(new Error(responseData.error));
          setEvents([]);
        } else {
          const events = responseData?.data || [];
          console.log(`[useFilteredEvents] ✅ ${events.length} événements récupérés`);
          
          if (events.length > 0) {
            // console.log('[useFilteredEvents] 📊 Aperçu des résultats (3 premiers):', 
            //   events.slice(0, 3).map(e => ({
            //     id: e.id,
            //     title: e.title,
            //     postal_code: e.postal_code,
            //     start_date: e.start_date,
            //     category: e.category?.name,
            //     age_group: e.age_group?.name
            //   }))
            // );
          } else {
            console.log('[useFilteredEvents] ⚠️  Aucun événement trouvé avec ces critères');
          }
          
          setEvents(events);
        }
      } catch (err) {
        console.error('[useFilteredEvents] 💥 Exception:', err);
        setError(err);
        setEvents([]);
      } finally {
        setLoading(false);
        console.log('[useFilteredEvents] ✔️  Chargement terminé');
      }
    };

    fetchEvents();
  }, [
    enabled,
    searchPosition,
    postalCodesKey, // Utiliser la clé stable au lieu du tableau
    filterDate,
    filterEndDate,
    JSON.stringify(filterCategories), // Stabiliser le tableau
    filterAgeGroup,
    filterAllWeather,
    filterAccessible,
    filterFree
  ]);

  return { events, loading, error };
};
